import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CrudList } from '../../shared/Crud';
import employeeService from '../../../services/employeeService';
import { DELIVERY_REQUESTER_LIST_COLUMNS } from './deliveryRequesterListColumns';
import { SELECTOR_MODAL_HINT, SelectorModalHint } from '../SelectorModalHint';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: ${({ $zIndex }) => $zIndex ?? 9999};
`;

const ModalBox = styled.div`
    box-sizing: border-box;
    background: #fff;
    padding: 24px;
    border-radius: 12px;
    width: 100%;
    max-width: min(900px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    overflow: hidden;
    min-width: 0;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-shrink: 0;

    h2 {
        margin: 0;
        font-size: 18px;
        color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #666;
    font-size: 24px;
    line-height: 1;
    &:hover {
        color: #222;
    }
`;

function pageContent(result) {
    if (Array.isArray(result?.content)) return result.content;
    if (Array.isArray(result?._embedded?.content)) return result._embedded.content;
    return [];
}

export default function DeliveryRequesterSelectorModal({
    open,
    customerId,
    /** Pessoa jurídica omite customerId; transportadora envia o cliente da entrega. */
    includeCustomerId = false,
    currentRequesterId,
    onClose,
    onSelectRequester,
    isSubmitting = false,
    zIndex = 9999,
}) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });

    useEffect(() => {
        if (!open) return;
        setData([]);
        setPagination({ page: 0, totalPages: 0, first: true, last: true });
        setLoadError(null);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (includeCustomerId && (customerId == null || String(customerId).trim() === '')) return;

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const result = await employeeService.getSolicitantes({
                    customerId: includeCustomerId ? customerId : undefined,
                    page: pagination.page,
                    size: 10,
                    sort: 'name,asc',
                });

                if (cancelled) return;

                const content = pageContent(result);
                const totalPages =
                    result?.page?.totalPages != null && Number.isFinite(Number(result.page.totalPages))
                        ? Number(result.page.totalPages)
                        : content.length === 0
                          ? 0
                          : 1;

                const pageNumber =
                    result?.page?.number != null && Number.isFinite(Number(result.page.number))
                        ? Number(result.page.number)
                        : pagination.page;

                setData(content);
                setPagination({
                    page: pageNumber,
                    totalPages,
                    first: totalPages === 0 || pageNumber === 0,
                    last: totalPages === 0 || pageNumber >= totalPages - 1,
                });
            } catch (e) {
                if (!cancelled) {
                    setLoadError(e?.message || 'Não foi possível carregar os solicitantes.');
                    setData([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, includeCustomerId, customerId, pagination.page]);

    const handlePageChange = (newPage) => {
        setPagination((p) => ({ ...p, page: newPage }));
    };

    const isRowDisabled = (item) => {
        if (isSubmitting) return true;
        return currentRequesterId != null && String(currentRequesterId) === String(item?.id);
    };

    if (!open) return null;

    return (
        <Overlay
            $zIndex={zIndex}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delivery-requester-modal-title"
            onClick={(e) => e.target === e.currentTarget && !isSubmitting && onClose?.()}
        >
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <h2 id="delivery-requester-modal-title">Selecionar solicitante</h2>
                    <CloseBtn type="button" onClick={() => !isSubmitting && onClose?.()} title="Fechar">
                        ×
                    </CloseBtn>
                </ModalHeader>
                <SelectorModalHint>{SELECTOR_MODAL_HINT}</SelectorModalHint>

                {loadError && (
                    <div style={{ marginBottom: 12 }} role="alert">
                        <p style={{ margin: 0, color: '#e74c3c', fontSize: 14 }}>{loadError}</p>
                    </div>
                )}

                <CrudList
                    hideHeader
                    title="Solicitantes"
                    tableMinWidth="640px"
                    columns={DELIVERY_REQUESTER_LIST_COLUMNS}
                    data={data}
                    loading={loading}
                    onRowClick={(item) => onSelectRequester?.(item)}
                    isRowDisabled={isRowDisabled}
                    pagination={{
                        ...pagination,
                        onPageChange: handlePageChange,
                    }}
                />
            </ModalBox>
        </Overlay>
    );
}
